import { Flex, IconWithVariant } from '@auviknetworks/iceberg-ui';
import { Button, Skeleton, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { WorkatoConnectionsModal } from 'application/Home/Admin/Workato/WorkatoConnectionsModal';
import { Notify } from 'application/Shared/Helpers';
import { TFunction } from 'i18next';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { getDateTime } from '../Core';
import { useManagedTableV2Context } from '../Table/ManagedTableV2ContextProvider';
import { DarkTooltip, LightTooltip } from '../Tooltips';
import { getLicenseDataStatus } from './functions';
import { LicenseDataStatus, LicenseProps } from './types';

const getVisualStatuses = ({
    dateLastSynced,
    setOpenModal,
    t
}: {
    dateLastSynced: string;
    setOpenModal: (open: boolean) => void;
    t: TFunction<'translation', undefined>;
}): Record<LicenseDataStatus, JSX.Element> => ({
    // Green check with tooltip of last sync date
    [LicenseDataStatus.available]: (
        <Box
            display="flex"
            alignItems="center"
            data-trackid="licenseDataAvailable"
        >
            <DarkTooltip
                arrow
                placement="right"
                title={t('LicenseData.status.sync.message', {
                    date: getDateTime({ date: dateLastSynced })
                })}
            >
                <Box>
                    <IconWithVariant
                        variant="success"
                        src="CheckCircle"
                        size="20px"
                    />
                </Box>
            </DarkTooltip>
        </Box>
    ),
    // Red circle with tooltip to connect to the integration
    [LicenseDataStatus.notConnected]: (
        <Flex alignItems="center" data-trackid="licenseDataNotConnected">
            <LightTooltip
                arrow
                placement="right"
                title={
                    <Flex alignItems="center">
                        <Typography>
                            {t('LicenseData.status.notConnected.message')}
                        </Typography>
                        <Box>
                            <Button
                                data-trackid="licenseData_configure"
                                onClick={() => setOpenModal(true)}
                                variant="contained"
                                color="primary"
                                size="slim"
                            >
                                {t('Common.configure')}
                            </Button>
                        </Box>
                    </Flex>
                }
            >
                <Box>
                    <IconWithVariant
                        variant="danger"
                        src="BanCircle"
                        size="20px"
                    />
                </Box>
            </LightTooltip>
        </Flex>
    ),
    // Red triangle with tooltip of last sync date
    [LicenseDataStatus.syncFailed]: (
        <Flex alignItems="center" data-trackid="licenseDataSyncFailed">
            <DarkTooltip
                arrow
                placement="right"
                title={t('LicenseData.status.sync.message', {
                    date: getDateTime({ date: dateLastSynced })
                })}
            >
                <Box>
                    <IconWithVariant
                        variant="danger"
                        src="WarningTriangle"
                        size="20px"
                    />
                </Box>
            </DarkTooltip>
        </Flex>
    ),
    // Unavailable text
    [LicenseDataStatus.unavailable]: (
        <Flex alignItems="center" data-trackid="licenseDataUnavailable">
            {t('Common.unavailable')}
        </Flex>
    )
});

export const LicenseData = ({ tenantApp }: LicenseProps) => {
    const { t } = useTranslation();
    const [openModal, setOpenModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const { clientId = '' } = useParams();
    const { setRerender } = useManagedTableV2Context();

    const { tenantLicenseTier } = tenantApp;
    const { name, workatoProvider } = tenantApp.application;

    const status = getLicenseDataStatus(tenantApp);

    // Get the max date of the last synced tier
    const dateLastSynced = tenantLicenseTier?.reduce(
        (max, tier) => (tier.dateLastSynced > max ? tier.dateLastSynced : max),
        tenantLicenseTier[0]?.dateLastSynced
    );

    const statuses = getVisualStatuses({
        dateLastSynced: dateLastSynced ?? '',
        setOpenModal,
        t
    });

    const handleSuccess = () => {
        setOpenModal(false);
        if (setRerender) {
            setLoading(true);
            setTimeout(() => {
                setRerender(true);
                setLoading(false);
            }, 2000);
        }
        Notify.s(
            t('SaasOps.integration.connection.success', {
                appName: name
            })
        );
    };

    return (
        <>
            {loading ? (
                <Skeleton variant="text" width={100} height={20} />
            ) : (
                statuses[status]
            )}
            {openModal && (
                <WorkatoConnectionsModal
                    clientId={clientId}
                    workatoProvider={workatoProvider}
                    displayName={name}
                    setOpenModal={setOpenModal}
                    onSuccessFn={handleSuccess}
                />
            )}
        </>
    );
};
